import { SearchType } from '@aws-sdk/client-bedrock-agent-runtime';
import type { FileSummary } from './cors-provenance-api';

export type { FileSummary };

export type View = 'chat' | 'table' | 'presentation' | 'courtroom' | 'horizontal' | 'settings' | 'sandbox' | 'work-product-index' | 'threat-report' | 'csam-report' | 'cases' | 'case-file-management' | 'account';

// Helper function to check if a view is a work product view.
// NOTE: 'csam-report' is intentionally excluded — the CSAM Dashboard is a
// standalone, reviewer-gated global nav destination (not a work product), so it
// must not be saved/restored as `lastWorkProductContext` or highlight the Work
// Products nav entry. Reviewers reopen it via the global "CSAM" nav item.
export const isWorkProductView = (view: View): boolean => {
  return view === 'table' || view === 'presentation' || view === 'courtroom' || view === 'horizontal' || view === 'threat-report' || view === 'work-product-index';
};

export type WorkProductType = 'timeline' | 'threat-report' | 'csam-report';

export type VerificationState = 'verified' | 'unverified';

export type RemoteStorageType = 'GoogleDrive' | 'OneDrive' | 'SharePoint';

export type ThreatReportKey = {
  chatKey: string;
  sessionId: string;
  epochTimeSecs: number;
  requestType: string;
}

export interface WorkProduct {
  id: string;
  type: WorkProductType;
  title: string;
  description?: string;
  isRead: boolean;
  verificationState: VerificationState;
  createdAt?: string;
  createdBy?: string;
  // For timelines, this will be the timeline ID
  // For threat reports, this will be the report ID
  resourceId?: string;
  eventCount?: number;
  status?: string;
  /** Selector ids a threat report was scoped to. Empty means the whole export. */
  creatorIds?: string[];
  /** Fivecast export the threat report was generated from. */
  fivecastDataDirectory?: string | null;
  /** This version is visible to the organization on the case. */
  shared?: boolean;
  /** The signed-in analyst generated this version. */
  isOwner?: boolean;
}

export type MediaType = 'image' | 'video' | 'audio' | 'pdf' | 'docx' | 'pptx' | 'document' | 'url' | 'youtube' | 'website' | 'text' | 'markdown' | 'html' | 'json' | 'unknown' | 'spreadsheet';

export type JSONValue =
    | string
    | number
    | boolean
    | undefined
    | null
    | { [x: string]: JSONValue }
    | Array<JSONValue>;

export interface JSONObject {
  [x: string]: JSONValue;
}

export interface JSONArray extends Array<JSONValue> { }

export type RemoteStorage = {
  type: string;
  url: string;
}

export type FeatureFlagTypes =
  JSONValue | JSONObject | JSONArray;

export type SourceContext = Record<string, any>;

/**
 * Enriched mobile artifact locator attached to a chat citation.
 * Other locator keys (thread id, order, group-by) still ride the index signature.
 */
export type MobileEvidence = {
  id: string;
  kind: string;
  view?: string;
  mobileAcquisitionId?: number;
  mobileArtifactId?: number;
  mobileArtifactIds?: number[];
  queryName?: string | null;
  artifactType?: string | null;
  label?: string | null;
  /** One sentence about what this cited query did or did not contain. */
  conclusion?: string | null;
  /** GraphQL artifact type the viewer opens for this locator. */
  graphqlType?: string | null;
  /** ``groupBy``, ``aggregate``, ``count``, or a row query. */
  queryShape?: string | null;
  /** Prisma-style ``where`` sent with a query or absence locator. */
  where?: Record<string, unknown> | null;
  /** Rows to ring when the cited set is wider than the highlighted subset. */
  highlightedArtifactIds?: number[] | null;
  [key: string]: unknown;
};

export type TimezoneDetails = {
  name: string;
  abbreviation: string | undefined;
  offset: string | undefined;
  offsetInMilliseconds: number | undefined;
}

export type ScopesObject = {
  scopes: string[];
  files: string[];
  excludedScopes: string[];
  excludedFiles: string[];
  /** Merged included category tokens sent to assistant chat. */
  contentCategories?: string[];
  /** Merged excluded category tokens sent to assistant chat. */
  excludedContentCategories?: string[];
  /** Evidence locker chip keys (e.g. PDF) — persisted with scopesObject. */
  evidenceCategoryChips?: string[];
  /** Included file-type scope keys from the filter picker (e.g. PDF). */
  evidenceCategoryScopes?: string[];
  /** Excluded file-type scope keys from the filter picker (e.g. PDF). */
  excludedEvidenceCategoryScopes?: string[];
  /** Evidence locker "All Files" — persisted with scopesObject. */
  evidenceAllFileTypes?: boolean;
}

export type EvidenceObject = {
  url?: string;
  mediaType?: MediaType;
  extension?: string;
  isSupportedMedia?: boolean;
  isSupportedExtension?: boolean;
  status?: string;
  fileInfo?: FileSummary | null;
}

/**
 * Unified view-model for any "file you can show in the UI" — chat-citation
 * preview, timeline-event media, evidence-locker row, share-tray entry, etc.
 *
 * Collapses the legacy ``MediaObject`` and ``SourceObject`` split into one
 * shape. All fields are optional because different call paths populate
 * different subsets (e.g. chat sources don't carry a ``mediaType`` until
 * ``getMediaType`` resolves it; evidence-locker rows carry ``sourceUrl``
 * but no display fields).
 *
 * Open follow-up cleanup (intentionally out of scope here):
 *  - ``mediaContext`` vs ``sourceContext``: populated by independent code
 *    paths today (chat citations vs. timeline events) and read by 16+ and
 *    14+ files respectively. Collapsing to a single field requires a
 *    separate audit pass — current behaviour is preserved.
 *  - URL precedence cascade (``mediaUrl`` / ``mediaSrc`` / ``srcToUse`` /
 *    ``originalUrl`` / ``presignedUrl`` / ``sourceUrl`` + the new
 *    ``fileInfo.fileUrl`` / ``fileInfo.previewUrl``) is six-deep in
 *    ``getSrcToUse``. Likely collapses to ``(srcUrl, originalUrl,
 *    presigned: boolean)`` once every caller is audited; deferred to a
 *    separate cleanup pass.
 */
export interface MediaSource {
  index?: number;
  status?: string;

  // Identity / pointers
  sourceType?: string;
  sourceUrl?: string;
  sourceFilePath?: string;
  presignedUrl?: string;
  originalUrl?: string;
  isReconstructedUrl?: boolean;
  truncatedFilePath?: string;
  previewFilePath?: string;
  transcriptUrl?: string;
  presignedTranscriptUrl?: string;
  dispatchedFilePath?: string;
  analysisFilePath?: string;
  derived_object_url?: string;
  s3Url?: string;
  /**
   * Provenance ``fileStatusId`` for the parent container (or self). Used to
   * re-fetch tree children when a virtual ``…/archive.zip/inner`` path has no
   * playable ``fileUrl`` yet.
   */
  fileStatusId?: string;

  // Display
  mediaType?: MediaType;
  mediaSrc?: string;
  mediaUrl?: string;
  srcToUse?: string;
  mediaContext?: SourceContext;
  sourceContext?: SourceContext;
  /**
   * Stable identity of the UI citation instance that opened this media.
   *
   * Two chat answers may cite the same file and page. Capture state must not
   * cross that answer boundary, so SourceCard stamps its answer/citation slot
   * here and fullscreen copies carry it with the media item.
   */
  captureCitationScope?: string;

  pageNumber?: number;
  content?: string;

  // Canonical file metadata (from provenance-service /files/file-info).
  fileInfo?: FileSummary | null;

  // Content-moderation / CSAM additions
  sourceInfo?: SourceInfo | null | undefined;
  /** When true, MediaDisplay skips the content-moderation preview override when fetching source info. */
  bypassCm?: boolean;
  /** Mobile artifact locator copied through from a chat Source. */
  evidence?: MobileEvidence;
}


export type UserPermissionObject = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  timeout?: number;
  body?: object;
  /**
   * Force the regular user-scope base path (``/tenants/{tenantId}``) even for
   * CSM/admin callers, skipping the ``/csm`` prefix. Use for endpoints that
   * live on the non-CSM router and handle staff internally (e.g.
   * ``/user/cases``).
   */
  forceUserScope?: boolean;
  /**
   * Force the CSM base path (``/csm/tenants/{tenantId}``) regardless of the
   * caller's Cognito group. Use for endpoints that only exist on the CSM
   * router (e.g. case management), so the request targets the documented path
   * and permission-service applies its CSM access check (a non-CSM caller gets
   * a clean 403 instead of being routed to a non-existent tenant path).
   */
  forceCsmScope?: boolean;
}

export type SourceInfo = {
  isAudioVideo?: boolean;
  isImage?: boolean;
  transcriptObjectUrl?: string;
  contentCategories?: string[];
  scope?: string[];
  sourceCaseId?: string;
  sourceObjectUrl?: string;
  sourceContentDigest?: string;
  sourceContentType?: string;
  sourceFileType?: string;
  sourceFileSize?: number;
  sourceUrlHash?: string;
  sourceUrlUuid?: string;
  previewObjectUrl?: string;
  previewContentDigest?: string;
  previewContentType?: string;
  previewFileType?: string;
  previewFileSize?: number;
  previewUrlHash?: string;
  previewUrlUuid?: string;
  downloadObjectUrl?: string;
  downloadContentDigest?: string;
  downloadContentType?: string;
  downloadFileType?: string;
  downloadFileSize?: number;
  downloadUrlHash?: string;
  downloadUrlUuid?: string;
  sourcePageNumber?: number;
  sourcePageCount?: number;
  analysisFilePath?: string | undefined;
  /** Content-moderation detection type (e.g. ``'CSAM'``) returned by the provenance service. */
  contentModerationType?: string;
  exp?: number; // this is used as a TTL for saving this in sessionStorage
}

export type MboxAttachment = {
  attachmentIndex: number;
  attachmentUrl: string;
  fileName: string;
  contentType: string | null;
  contentLength: number;
}

export type MboxEmail = {
  emailIndex: number;
  mboxUrl: string;
  emailUrl: string;
  attachmentUrl: string | null;
  subject: string;
  fromAddress: string;
  toAddresses: string[];
  ccAddresses: string[];
  bccAddresses: string[];
  date: string;
  dateRaw: string;
  attachmentCount: number;
  attachments: MboxAttachment[];
}

export type Source = {
  index?: number | undefined;
  sourceType?: string | undefined;
  sourceUrl?: string | undefined;
  content?: string;
  location?: string;
  page?: string | undefined;
  pageNumber?: number | undefined;
  /** Total page count of the underlying document, when known upstream (e.g. from FileSummary). */
  pageCount?: number | null;
  sourceFilePath?: string | undefined;
  truncatedFilePath?: string | undefined;
  /** Human-readable hierarchical path including inner archive/mbox member identity. */
  searchablePath?: string | undefined;
  sourceContext?: SourceContext | undefined;
  presignedTranscriptUrl?: string | undefined;
  transcriptUrl?: string | undefined;
  previewFilePath?: string | undefined;
  /** Parent container URL (mbox/zip) when the citation was resolved to an inner extract. */
  originalUrl?: string | undefined;
  derived_object_url?: string;
  fileInfo?: FileSummary | null;
  analysisFilePath?: string | undefined;
  dispatchedFilePath?: string | undefined;
  /** When true, MediaDisplay skips the content-moderation preview override when fetching source info. */
  bypassCm?: boolean;
  /** Human-readable display name shown in the SourceCard header instead of the path derived from sourceUrl. */
  label?: string;
  /** Mobile artifact locator. Without a file URL, the citation pane hydrates this into a table, thread, or map. */
  evidence?: MobileEvidence;
};

/**
 * File the assistant generated during a chat turn (markdown report, CSV
 * export, etc.) and uploaded to S3. Rendered as a download chip on the
 * answer card; the URL is signed at click time via the assistant-service
 * `/chat/attachments/sign` endpoint.
 */
export interface ChatAttachment {
  filename: string;
  s3_key: string;
  mime_type: string;
  size_bytes: number;
  kind: string;
}

/**
 * Coerce the raw `attachments` sibling field off a chat-history row into a
 * validated `ChatAttachment[]`. The row is stored as an untyped DynamoDB
 * attribute and reaches the UI via several paths (bulk history load, per-row
 * fetch, sessionStorage cache), so callers must not trust its shape. Rows
 * missing the field, or entries missing the two fields the UI relies on
 * (`filename` for display, `s3_key` for signing), are dropped.
 */
export function parseChatAttachments(raw: unknown): ChatAttachment[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (a: unknown): a is ChatAttachment =>
      !!a
      && typeof a === 'object'
      && typeof (a as ChatAttachment).filename === 'string'
      && typeof (a as ChatAttachment).s3_key === 'string',
  );
}

/** Entity extraction BFF helper from ``main.tsx`` — keep call sites aligned (``cancelPolling`` then ``initiator``). */
export type GetEventsFromEntityService = (
  action: string,
  timelineId: string | null,
  caseId: string,
  answerItemText: string,
  sources: Source[],
  epochTimeSecs: number,
  jobId: string | null,
  cancelPolling: boolean,
  initiator: 'user' | 'system',
) => Promise<void>;

export interface Slide {
  approval?: string;
  show: boolean;
  /**
   * When true, the event is hidden from the horizontal timeline view only
   * (it still appears in presentation/table). Independent of `show`.
   * Persisted server-side via the `hiddenFromTimeline` payload field when the
   * backend supports it, with a localStorage fallback (see shared/timeline-visibility.ts).
   */
  hideFromTimeline?: boolean;
  id?: string;
  displayOrder?: number;
  order?: number;
  date?: string;
  displayDate?: string;
  isTimeValid?: boolean;
  time?: string;
  localizedDateTime?: string; // i.e. convert to local timezone
  slideTitle?: string;
  slideSubTitle?: string;
  slideDescription?: string;
  courtroomTitle?: string;
  courtroomDescription?: string;
  mediaType?: MediaType;
  mediaSrc?: string | null;
  mediaContext?: SourceContext;
  media?: MediaSource[];
  eventType?: string;
  evidenceSrc?: string;
  location?: {
    coordinates: [number, number] | null;
    name: string | null;
    address: string | null;
  };
  sources?: MediaSource[];
  eventLocalTimezone?: string;
  createdAt?: string;
  updatedAt?: string;
}


export interface DateItem {
  date: string;
  displayDate?: string;
  displayOrder?: number;
  events: Slide[];
  show?: boolean;

}

export interface TimelinePatch {
  tags: string[];
  title?: string;
  description?: string;
  courtroomTitle?: string;
  courtroomDescription?: string;
  media?: MediaSource[] | string | undefined;
  slideTitle?: string;
  slideDescription?: string;
}

export interface LLMConfig {
  refusalMessages: string[],
  textResponsePartDelimiter: string,
  overheadPerPair: number,
  markdownDirective: string,
  promptTemplate: string,
  conversationContext: string,
  promptTemplateBase: string,
  promptTemplateQuery: string,
  promptTemplateWithContext: string,
  orchestrationPromptTemplate: string,
  historyWordLimit: number,
  historyCharLimit: number,
  temperature: number,
  unalignedTemperature: number,
  maxTokens: number,
  numOfResults: number,
  overrideSearchType: SearchType | undefined,
  supportedQueryTransformationConfigTypes: string[],
  passMessages: string[],
  topP: number,
}

// copied from chat/route.ts - may need updated for new timelines
export const SOURCE_TYPES = [
  'DataSyncMT',
  'DataSyncUsb',
  'DataSyncNet',
  'GoogleDrive',
  'OneDrive',
  'Manual',
  'Textract',
  'Unknown',
]

// saving these for reference but using just the /bkb/slow/ for now, since it is in all of them
export const SOURCE_TYPES_TEXTRACT = [
  '/bkb/slow/',
  '/converse_image/',
  '/custom_text_extraction/',
  '/original_content/',
  '/textract/',
  '/word_doc_embedded_images/',
]

export interface TimelineEvent {
  deletedAt: string | null;
  deletedBy: string | null;
  deleted: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  eventEntityId: string | null;
  eventDateTime: string;
  eventLocalTimezone: string;
  isTimeValid: boolean;
  displayOrder: number;
  slideTitle: string;
  slideSubTitle: string | null;
  slideDescription: string;
  courtroomTitle?: string | null;
  courtroomDescription?: string | null;
  location: string | null;
  sources: MediaSource[];
  media: MediaSource[];
  hidden: boolean;
  approved: boolean;
  id: string;
  hiddenAt: string | null;
  hiddenBy: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
}

export interface Timeline {
  deletedAt: string | null;
  deletedBy: string | null;
  deleted: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  title: string;
  description: string;
  courtroomTitle?: string | null;
  courtroomDescription?: string | null;
  tags: string[];
  isGenerated: boolean;
  showTitle: boolean;
  events: TimelineEvent[];
  id: string;
}

export interface TimelineResponse {
  timeline: Timeline;
  sources: MediaSource[];
  media: MediaSource[];
  timelineList: Timeline[];
}

export type StatusEnum = 'active' | 'inactive' | 'pending' | 'suspended';

export interface UserPreferencesDTO {
  preferences: Record<string, any> | null;
}

export interface GroupMembershipDTO {
  id: string;
  user_id: string;
  group_id: string;
  joined_at: string;
  is_active: boolean;
}

export interface GroupSummaryDTO {
  id: string;
  name: string;
  description: string | null;
  tenant_id: string;
  status: StatusEnum;
}

export interface CaseSummaryDTO {
  id: string;
  name: string;
  description: string | null;
  tenant_id: string;
  owner_id: string;
  status: StatusEnum;
}

export interface UserDTO {
  id: string;
  email: string;
  name: string;
  status: StatusEnum;
  tenant_id: string;
  last_login: string | null;
  is_admin: boolean;
  /** Permission-service CSAM limited-user clearance (canonical TR-3175 name). */
  csam_limited_user?: boolean;
  /** Permission-service CSAM verifier clearance — original-image reveal. */
  csam_verifier?: boolean;
  /** Legacy alias for ``csam_limited_user``. */
  csam_viewer?: boolean;
  /** Legacy alias for ``csam_verifier``. */
  csam_reviewer?: boolean;
  preferences: UserPreferencesDTO | null;
  group_memberships: GroupMembershipDTO[];
  groups: GroupSummaryDTO[] | null;
  cases: CaseSummaryDTO[] | null;
  // Cognito status information
  cognito_user_status?: string | null;
  cognito_enabled?: boolean | null;
  cognito_user_create_date?: string | null;
  cognito_days_since_creation?: number | null;
  cognito_needs_password_reset?: boolean | null;
}


export interface CreateOrganizationRequest {
  name: string;
  contact_email?: string;
  auth_architecture: string;
  domain?: string;
  status: string;
  cognito_user_pool_arn?: string;
  cognito_domain?: string;
  app_client_id?: string;
}

export interface OrganizationDTO {
  id: string;
  external_id: string;
  name: string;
  contact_email?: string;
  auth_architecture: string;
  domain?: string;
  status: string;
  cognito_user_pool_arn?: string;
  cognito_domain?: string;
  app_client_id?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  tenant_count?: number;
  /** Whether users can add tenants (current org and knowledge base ready). */
  can_add_tenants?: boolean;
  /** True when this org is the current org in tenant_pool_state. */
  is_current_organization?: boolean;
  /** From tenant_pool_state when this org is current. */
  knowledge_base_ready?: boolean | null;
  /** UI status line from backend (e.g. "Current · Accepting tenants"). */
  status_message?: string | null;
  /** Hint for UI styling: success (green), warning (amber), muted (slate). */
  status_class?: 'success' | 'warning' | 'muted' | null;
}

export interface OrganizationListResponse {
  organizations: OrganizationDTO[];
  total: number;
}

export interface UpdateOrganizationRequest {
  name: string;
  contact_email?: string;
  auth_architecture: string;
  domain?: string;
  status: string;
  cognito_user_pool_arn?: string;
  cognito_domain?: string;
  app_client_id?: string;
}

export interface TenantDTO {
  id: string;
  external_id?: string;
  organization_id: string;
  tenant_name: string;
  tenant_code: string;
  timezone: string;
  contact_email?: string;
  status: string;
  s3_path?: string;
  cognito_user_pool_arn?: string;
  cognito_domain?: string;
  app_client_id?: string;
  avp_store_id?: string;
  avp_identity_source_id?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  banners: string[];
  remote_storage_type?: RemoteStorageType;
  remote_storage_url?: string;
  multi_tenant_friendly_name?: string;
  organization: OrganizationDTO;
}

export interface CreateTenantRequest {
  organization_id: string;
  tenant_name: string;
  tenant_code: string;
  timezone: string;
  contact_email?: string;
  banners: string[];
  remote_storage_type?: RemoteStorageType;
  remote_storage_url?: string;
  multi_tenant_friendly_name?: string;
}

export interface TenantListResponse {
  tenants: TenantDTO[];
  total: number;
}

export interface UpdateTenantRequest {
  tenant_name: string;
  timezone: string;
  contact_email?: string;
  banners: string[];
  remote_storage_type?: RemoteStorageType;
  remote_storage_url?: string;
  multi_tenant_friendly_name?: string;
}

export const AUTH_ARCHITECTURE_OPTIONS = [
  { value: 'cognito-single-pool', label: 'Cognito Single Pool' },
  { value: 'cognito-multi-tenant', label: 'Cognito Multi-Tenant' },
];


export const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern' },
  { value: 'America/Denver', label: 'Mountain' },
  { value: 'America/Chicago', label: 'Central' },
  { value: 'America/Los_Angeles', label: 'Pacific' },
];
